import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import MediaModal from './MediaModal';
import { mediaMetadata } from '../data/projects';

import './Masonry.css';

const metadata = mediaMetadata;

const useMedia = (queries: string[], values: number[], defaultValue: number): number => {
  const get = useCallback(() => values[queries.findIndex(q => matchMedia(q).matches)] ?? defaultValue, [queries, values, defaultValue]);

  const [value, setValue] = useState<number>(get);

  useEffect(() => {
    const handler = () => setValue(get);
    queries.forEach(q => matchMedia(q).addEventListener('change', handler));
    return () => queries.forEach(q => matchMedia(q).removeEventListener('change', handler));
  }, [queries, get]);

  return value;
};

const useMeasure = <T extends HTMLElement>() => {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, size] as const;
};

const DEFAULT_DIMENSIONS = { w: 400, h: 300 };

interface Item {
  id: string;
  src: string;
  type: 'image' | 'video';
  alt?: string;
  url?: string;
}

interface GridItem extends Item {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Video lazy: carica quando vicino al viewport, play solo quando visibile, pause fuori
const LazyVideo: React.FC<{ src: string; className: string; poster?: string; width?: number; height?: number; onLoadedData?: (e: React.SyntheticEvent<HTMLVideoElement>) => void }> = ({ src, className, poster, width, height, onLoadedData }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasLoaded(true);
          loadObserver.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    loadObserver.observe(el);

    const playObserver = new IntersectionObserver(
      ([entry]) => {
        const v = videoRef.current;
        if (!v) return;
        if (entry.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    playObserver.observe(el);

    return () => { loadObserver.disconnect(); playObserver.disconnect(); };
  }, []);

  return (
    <video
      ref={videoRef}
      src={hasLoaded ? src : undefined}
      className={className}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      onLoadedData={onLoadedData}
    />
  );
};

interface MasonryProps {
  items: Item[];
  ease?: string;
  duration?: number;
  stagger?: number;
  animateFrom?: 'bottom' | 'top' | 'left' | 'right' | 'center' | 'random';
  scaleOnHover?: boolean;
  hoverScale?: number;
  columns?: number;
  blurToFocus?: boolean;
  colorShiftOnHover?: boolean;
}

const Masonry: React.FC<MasonryProps> = ({
  items,
  ease = 'power3.out',
  duration = 0.6,
  stagger = 0.05,
  animateFrom = 'bottom',
  scaleOnHover = true,
  hoverScale = 0.95,
  columns: customColumns,
  blurToFocus = false,
  colorShiftOnHover = false
}) => {
  const responsiveColumns = useMedia(
    ['(min-width:1500px)', '(min-width:1000px)', '(min-width:600px)', '(min-width:400px)'],
    [5, 4, 3, 2],
    1
  );
  
  const columns = customColumns ?? responsiveColumns;

  const [containerRef, { width }] = useMeasure<HTMLDivElement>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState<Item | null>(null);

  const getInitialPosition = useCallback((item: GridItem) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { x: item.x, y: item.y };

    let direction = animateFrom;

    if (animateFrom === 'random') {
      const directions = ['top', 'bottom', 'left', 'right'];
      direction = directions[Math.floor(Math.random() * directions.length)] as typeof animateFrom;
    }

    switch (direction) {
      case 'top':
        return { x: item.x, y: -200 };
      case 'bottom':
        return { x: item.x, y: window.innerHeight + 200 };
      case 'left':
        return { x: -200, y: item.y };
      case 'right':
        return { x: window.innerWidth + 200, y: item.y };
      case 'center':
        return {
          x: containerRect.width / 2 - item.w / 2,
          y: containerRect.height / 2 - item.h / 2
        };
      default:
        return { x: item.x, y: item.y + 100 };
    }
  }, [animateFrom, containerRef]);

  // Gestione migliorata per mobile
  const isMobile = useMemo(() => {
    return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  const grid = useMemo<GridItem[]>(() => {
    if (!width) return [];

    const colHeights = new Array(columns).fill(0);
    const columnWidth = width / columns;

    let gap = 48;
    if (width <= 1200) gap = 44;
    if (width <= 768) gap = 40;

    const availableWidth = columnWidth - gap;

    return items.map(child => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const meta = metadata[child.src] ?? DEFAULT_DIMENSIONS;
      const aspectRatio = meta.h / meta.w;
      const safeAspect = isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
      const height = availableWidth * safeAspect;

      const x = columnWidth * col + (gap / 2);
      const y = colHeights[col] + (gap / 2);
      colHeights[col] += height + gap;

      return { ...child, x, y, w: availableWidth, h: height };
    });
  }, [columns, items, width]);

  const imagesReady = width > 0;

  const hasMounted = useRef(false);

  useLayoutEffect(() => {
    // In modalità mobile usiamo lo stack senza animazioni GSAP sugli elementi posizionati
    if (!imagesReady || isMobile) return;

    grid.forEach((item, index) => {
      const selector = `[data-key="${item.id}"]`;
      const animationProps = {
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h
      };

      if (!hasMounted.current) {
        const initialPos = getInitialPosition(item);
        const initialState = {
          //opacity: 0,
          x: initialPos.x,
          y: initialPos.y,
          width: item.w,
          height: item.h,
          ...(blurToFocus && !isMobile && { filter: 'blur(10px)' }) // Disabilita blur iniziale su mobile
        };

        gsap.fromTo(selector, initialState, {
          //opacity: 1,
          ...animationProps,
          ...(blurToFocus && !isMobile && { filter: 'blur(0px)' }), // Disabilita blur su mobile
          duration: isMobile ? 0.4 : 0.8, // Animazioni più veloci su mobile
          ease: 'power2.out', // Ease più semplice su mobile
          delay: isMobile ? index * 0.02 : index * stagger // Delay ridotto su mobile
        });
      } else {
        gsap.to(selector, {
          ...animationProps,
          duration: duration,
          ease: ease,
          overwrite: 'auto'
        });
      }
    });

    hasMounted.current = true;
  }, [grid, imagesReady, stagger, animateFrom, blurToFocus, duration, ease, getInitialPosition, isMobile]);

  const handleMouseEnter = (e: React.MouseEvent, item: GridItem) => {
    const element = e.currentTarget as HTMLElement;
    const selector = `[data-key="${item.id}"]`;

    if (scaleOnHover) {
      gsap.to(selector, {
        scale: hoverScale,
        duration: 0.3,
        ease: 'power2.out'
      });
    }

    if (colorShiftOnHover) {
      const overlay = element.querySelector('.color-overlay') as HTMLElement;
      if (overlay) {
        gsap.to(overlay, {
          opacity: 0.3,
          duration: 0.3
        });
      }
    }
  };

  const handleMouseLeave = (e: React.MouseEvent, item: GridItem) => {
    const element = e.currentTarget as HTMLElement;
    const selector = `[data-key="${item.id}"]`;

    if (scaleOnHover) {
      gsap.to(selector, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      });
    }

    if (colorShiftOnHover) {
      const overlay = element.querySelector('.color-overlay') as HTMLElement;
      if (overlay) {
        gsap.to(overlay, {
          opacity: 0,
          duration: 0.3
        });
      }
    }
  };

  const handleItemClick = (item: GridItem) => {
    // Apri sempre il modal per immagini e video
    setSelectedMediaItem(item);
    setIsModalOpen(true);
    // Aggiungi classe al body quando il modal è aperto
    document.body.classList.add('modal-open');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMediaItem(null);
    // Rimuovi classe dal body quando il modal è chiuso
    document.body.classList.remove('modal-open');
  };

  return (
    <div ref={containerRef} className={isMobile ? 'masonry-stack' : 'masonry-list'}>
      {isMobile ? (
        items.map((item) => (
          <div key={item.id} className="stack-item" onClick={() => handleItemClick({ ...item, x: 0, y: 0, w: 0, h: 0 } as GridItem)} style={{ cursor: 'pointer' }}>
            {item.type === 'image' ? (
              <img
                src={item.src}
                alt={item.alt || `Project ${item.id}`}
                className="stack-img"
                loading="lazy"
                decoding="async"
                width={metadata[item.src]?.w}
                height={metadata[item.src]?.h}
              />
            ) : (
              <LazyVideo
                // Nella griglia parte la versione leggera, quando esiste: sono
                // molti video che partono insieme. La versione piena resta e
                // viene usata quando si apre il video a schermo intero.
                src={metadata[item.src]?.anteprima ?? item.src}
                className="stack-img video-item"
                poster={metadata[item.src]?.poster}
                width={metadata[item.src]?.w}
                height={metadata[item.src]?.h}
              />
            )}
          </div>
        ))
      ) : (
        grid.map(item => (
          <div
            key={item.id}
            data-key={item.id}
            className="masonry-item-wrapper"
            onMouseEnter={e => handleMouseEnter(e, item)}
            onMouseLeave={e => handleMouseLeave(e, item)}
            onClick={() => handleItemClick(item)}
            style={{ cursor: 'pointer' }}
          >
            {item.type === 'image' ? (
              <img
                src={item.src}
                alt={item.alt || `Project ${item.id}`}
                className="masonry-item-img"
                loading="lazy"
                decoding="async"
                width={metadata[item.src]?.w}
                height={metadata[item.src]?.h}
              />
            ) : (
              <LazyVideo
                // Come sopra: nella griglia la versione leggera, se c'e'.
                src={metadata[item.src]?.anteprima ?? item.src}
                className="masonry-item-img video-item"
                poster={metadata[item.src]?.poster}
                width={metadata[item.src]?.w}
                height={metadata[item.src]?.h}
                onLoadedData={(e) => {
                  const video = e.currentTarget as HTMLVideoElement;
                  try { if (video.currentTime < 0.1) video.currentTime = 0.1; } catch {}
                }}
              />
            )}
            {colorShiftOnHover && (
              <div
                className="color-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(45deg, rgba(255,0,150,0.5), rgba(0,150,255,0.5))',
                  opacity: 0,
                  pointerEvents: 'none',
                  borderRadius: '0'
                }}
              />
            )}
          </div>
        ))
      )}

      <MediaModal isOpen={isModalOpen} onClose={handleCloseModal} mediaItem={selectedMediaItem} />
    </div>
  );
};

export default Masonry;
